import { Button, Checkbox, Input, InputNumber, Modal, Tour, message } from 'antd';
import type { TourProps } from 'antd';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import BgRemovePanel from './BgRemovePanel';
import { BgSession, exportSession, hasVisiblePixels } from './bgremove';
import Marmotte from './Marmotte';
import { TutoLink } from './Tutos';
import { recommendVectorize } from './vectorize';
import type { LogoMetrics } from './logoMetrics';
import { DTFItem } from './packing';
import { fmtCm, QualityInfo } from './quality';

import { ThinFixMode } from './thinfix';
import { canUpscale, predictUpscalePx } from './upscale';
import { suggestName } from './library';

const round1 = (v: number) => Math.round(v * 10) / 10;
const commaParser = (v: string | undefined) => Number((v ?? '').replace(',', '.'));

/* Daniel 07.10.2026 : « une étape par procédure, clair pour un enfant de 10 ans ». Revue Neo 07.10 :
   une décision par écran, verdicts prudents, aucun chiffre technique en premier plan (dpi/pixels derrière
   « Comment le savons-nous ? »), les écrans qui n'ont rien à décider disparaissent, pas de visite guidée
   automatique. Écrans : taille → fond (images) → netteté (si risque) → traits (si repérés) → prêt.
   Daniel 09.10.2026 (revue Neo) : « trop petit, UX vraiment simple, rien de caché sur n'importe quel écran » →
   mobile d'abord : question en gros, aperçu compact, UNE action principale par écran, secondaires en vrais
   boutons (≥ 48 px), textes ≥ 15 px, pied de page toujours visible, corps qui défile (`.dtf-prep-scroll`). */
type StepKey = 'taille' | 'fond' | 'finition' | 'traits' | 'pret';
const ORDER: StepKey[] = ['taille', 'fond', 'finition', 'traits', 'pret'];
const PRESETS: { k: string; cm: number }[] = [
	{ k: 'coeur', cm: 8 },
	{ k: 'poitrine', cm: 20 },
	{ k: 'dos', cm: 30 },
	{ k: 'manche', cm: 7 },
];

interface IProps {
	item: DTFItem | null;
	quality?: QualityInfo;
	minDpi: number;
	thin: boolean;
	upscaling: boolean;
	fixingThin: boolean;
	laizeCm: number;
	maxPlancheCm: number;
	onChange: (id: string, patch: Partial<DTFItem>) => void;
	onApplyBg: (id: string, out: { src: string; aspect: number }) => void;
	onUpscale: (id: string) => void;
	/** lissage des bords sans vectoriser (Daniel 15.10) */
	onSmooth?: (id: string) => Promise<boolean>;
	smoothing?: boolean;
	onVectorize?: (id: string) => void;
	vectorizing?: boolean;
	metrics?: LogoMetrics | null;
	onFixThin: (id: string, mode: ThinFixMode, garmentColor?: string) => Promise<boolean>;
	/** done=true : parcours terminé ; false/undefined : fermé en cours de route (croix/masque). meta : choix de la dernière étape (bibliothèque) */
	onClose: (done?: boolean, meta?: { keep: boolean; name: string }) => void;
	/** connecté sur le site → la dernière étape propose de garder le logo dans la bibliothèque */
	canSave?: boolean;
}

const PrepWizard: React.FC<IProps> = ({
	item,
	quality,
	minDpi,
	thin,
	upscaling,
	fixingThin,
	laizeCm,
	maxPlancheCm,
	onChange,
	onApplyBg,
	onUpscale,
	onSmooth,
	smoothing = false,
	onVectorize,
	vectorizing = false,
	metrics = null,
	onFixThin,
	onClose,
	canSave = false,
}) => {
	const t = (k: string, o?: Record<string, unknown>) => i18next.t(`dtf.${k}`, o) as string;
	const [step, setStep] = useState<StepKey>('taille');
	const [byHeight, setByHeight] = useState(false);
	const [garmentColor, setGarmentColor] = useState('#ffffff');
	const [acceptBlur, setAcceptBlur] = useState(false);
	const [acceptThin, setAcceptThin] = useState(false);
	const [fondChoice, setFondChoice] = useState<'garde' | 'enleve' | null>(null);
	const [improvedHere, setImprovedHere] = useState(false);
	const [fixedHere, setFixedHere] = useState(false);
	// 20.10.2026 (Daniel) : le client doit VOIR que ça travaille, puis voir le résultat sous ses yeux — aperçu voilé + Paddy
	// pendant le traitement, bascule « Avant / Après » quand c'est fini.
	const [beforeSrc, setBeforeSrc] = useState<string | null>(null);
	const [showBefore, setShowBefore] = useState(false);
	const startAction = () => { if (item) setBeforeSrc(item.src); setShowBefore(false); };
	// « Épaissir » / « Fond sous les traits » : l'écran « Traits épaissis ! » ne s'affiche que si le logo a VRAIMENT changé.
	// 20.10.2026 (Daniel, logo « test ») : avant, la correction pouvait ne rien trouver (autre échelle d'analyse que la
	// détection) et l'écran disait quand même « Traits épaissis ! ». Si rien n'est à corriger à cette taille, l'alerte
	// n'a plus lieu d'être : on passe (la détection, qui voit les mêmes traits, ne crie pas non plus).
	const fixHere = async (mode: ThinFixMode) => {
		if (!item) return;
		startAction();
		setFixedHere(true);
		const changed = await onFixThin(item.id, mode, mode === 'backing' ? garmentColor : undefined);
		if (!changed) {
			setFixedHere(false);
			setAcceptThin(true);
		}
	};
	// Bibliothèque (Daniel 09.10 / 15.10) : à la fin, le client NOMME son logo et il est gardé (coché d'office)
	const [keep, setKeep] = useState(true);
	const [logoName, setLogoName] = useState(() => suggestName(item?.name || ''));
	const sessRef = useRef<BgSession | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [, setTick] = useState(0);
	const bump = () => setTick(v => v + 1);
	// Visite guidée : jamais automatique, seulement via ❓ (revue Neo 07.10)
	const [tourOpen, setTourOpen] = useState(false);

	const isVector = !!item && item.kind === 'svg';
	const q = item && quality && quality.src === item.src ? quality : undefined;
	const analysing = !!item && !q;
	// 20.10.2026 (logo de Daniel) : « Améliorer l'image » laissait passer à l'écran suivant pendant les 30-60 s de calcul ; un
	// épaississement lancé entre-temps travaillait sur la petite image puis était ÉCRASÉ par l'amélioration. Tout traitement en
	// cours bloque donc le changement d'écran, comme l'analyse.
	const busy = analysing || upscaling || !!vectorizing || !!smoothing || fixingThin;
	const busyKind: 'thin' | 'up' | 'vec' | 'smooth' | null = fixingThin ? 'thin' : upscaling ? 'up' : vectorizing ? 'vec' : smoothing ? 'smooth' : null;
	const dpiNow = q && item && !isVector ? Math.round(q.pxW / (item.widthCm / 2.54)) : null;
	const dpiBad = dpiNow !== null && dpiNow < minDpi;
	// Daniel 15.10 : l'amélioration (gratuite) est PROPOSÉE dès que l'image n'est pas très nette à la taille choisie (< 300 dpi),
	// et recommandée en dessous de 150 ; au-dessus de 300 elle n'apporte rien à l'impression → l'écran disparaît
	const SOFT_DPI = 300;
	const dpiSoft = dpiNow !== null && !dpiBad && dpiNow < SOFT_DPI && !!q && canUpscale(q.pxW, q.pxH);
	// traits : alerte réelle à la largeur choisie, ou traits trop fins à TOUTES les tailles ; le conflit « traits ≥ X cm mais
	// dpi ≤ Y cm » se règle par l'écran Netteté (sinon l'écran Traits resterait quelle que soit la largeur)
	// Daniel 09.10 : l'assistant doit dire la MÊME chose que la carte — « aucune largeur idéale » (traits ≥ X cm mais dpi ≤ Y cm)
	// passe par l'écran Netteté ; traits fins à la largeur choisie (largeur < minCm) par l'écran Traits, sans attendre l'analyse
	// asynchrone de la carte (prop `thin`)
	// « aucune largeur idéale » (traits ≥ X cm mais dpi ≤ Y cm) se règle d'abord par les traits (épaissir fait tomber X) →
	// écran Traits, comme la fiche qualité qui propose Épaissir dans ce cas (#225)
	const thinNow = !!item && (thin || (!!q && q.minCm !== null && q.minCm !== Infinity && item.widthCm < q.minCm - 1e-6));
	// rangeBad (min > max) n'impose l'écran Traits que tant que la largeur est sous minCm (thinNow) ; au-delà, c'est la netteté (dpiBad)
	const needTraits = !!item && (thinNow || (!!q && q.minCm === Infinity));
	// Vectoriser ? (Daniel 09.10) : proposé à tout logo raster, recommandé selon l'analyse ; « Garder tel quel » le saute.
	// Porte de service : ?cartes=toutes (suites e2e historiques) ou dtf_v2_no_vecto=1 → pas d'écran.
	const [acceptRaster, setAcceptRaster] = useState(false);
	// Compteur « Étape n sur N » stable (test humain 15.10, revue Neo) : les écrans visités et le total vivent dans l'ÉTAT, mis à
	// jour par goTo() — le seul chemin qui change d'écran — jamais pendant le rendu. Le total ne descend jamais ; la
	// composante est remontée par logo (key={wizardId}), donc tout repart de zéro pour un nouveau logo.
	const [visited, setVisited] = useState<StepKey[]>([]);
	const [stepTotalSeen, setStepTotalSeen] = useState(0);
	const vectoHidden = typeof window !== 'undefined' && (/[?&]cartes=toutes/.test(window.location.search) || localStorage.getItem('dtf_v2_no_vecto') === '1');
	const verdict = recommendVectorize(metrics);
	const steps: StepKey[] = [
		'taille',
		...(!isVector ? (['fond'] as StepKey[]) : []),
		// Daniel 20.10.2026 : après le retrait de couleur, UN écran « Finition » : points par pouce, analyse (unies / dégradés), et les trois
		// choix côte à côte (vectoriser / lisser sans vectoriser / améliorer) avec le conseil mis en avant. « Garder tel quel » le saute.
		// Porte de service (?cartes=toutes, dtf_v2_no_vecto) : l'écran ne reste que s'il y a un vrai risque de netteté.
		...(!isVector && !acceptRaster && (!vectoHidden || ((dpiBad || dpiSoft) && !acceptBlur)) ? (['finition'] as StepKey[]) : []),
		// 20.10.2026 (logo de Daniel) : une fois les traits épaissis (fixedHere), on ne redemande pas — la détection peut rester
		// allumée à cause de minuscules éclaboussures que l'épaississement ignore à dessein ; le récap dit « corrigés », et un
		// risque résiduel y est signalé.
		...((needTraits && !acceptThin && !fixedHere) || (fixedHere && step === 'traits') ? (['traits'] as StepKey[]) : []),
		'pret',
	];
	// « Continuer » demandé pendant que Paddy analyse (ex. juste après un retrait de fond : nouvelle image, nouvelle analyse) :
	// on attend la fin de l'analyse AVANT de choisir l'écran suivant — sinon on arrivait sur « tout est bon » puis la carte
	// disait « ce fichier pose problème »
	const [pendingNext, setPendingNext] = useState(false);
	// UNIQUE point de changement d'écran : tient le journal des écrans vus et le total affiché (jamais décroissant)
	const goTo = (k: StepKey) => {
		setVisited(v => (v.includes(k) ? v : [...v, k]));
		setStep(k);
	};
	// l'écran courant n'a plus rien à décider (ex. image améliorée) → on avance ; pas pendant une analyse
	useEffect(() => {
		if (busy || steps.includes(step)) return;
		const after = ORDER.slice(ORDER.indexOf(step) + 1).find(k => steps.includes(k)) || 'pret';
		goTo(after);
	}, [busy, step, steps.join('|')]);
	useEffect(() => {
		if (!pendingNext || busy) return;
		setPendingNext(false);
		const i = steps.indexOf(step);
		goTo(steps[Math.min(i + 1, steps.length - 1)]);
	}, [pendingNext, busy, step, steps.join('|')]);
	// chaque écran repart du haut
	useEffect(() => {
		if (scrollRef.current) scrollRef.current.scrollTop = 0;
	}, [step]);

	if (!item) return null;

	const go = (dir: 1 | -1) => {
		const i = steps.indexOf(step);
		const n = steps[Math.min(Math.max(i + dir, 0), steps.length - 1)];
		goTo(n);
	};
	const next = () => {
		if (step === 'fond') {
			const s = sessRef.current;
			if (s && s.dirty) {
				if (!hasVisiblePixels(s)) {
					message.error(t('detourage-vide'), 5);
					return;
				}
				onApplyBg(item.id, exportSession(s));
				message.success(t('detourage-ok'), 4);
				setFondChoice('enleve');
				setPendingNext(true); // nouvelle image → on attend son analyse avant de dire quoi que ce soit
				return;
			} else if (!fondChoice) setFondChoice('garde');
		}
		// on n'attend l'analyse que quand l'écran suivant en dépend (après le fond ; après la taille pour un vectoriel)
		if ((analysing && (step === 'fond' || (step === 'taille' && isVector))) || (step === 'finition' && busy)) {
			setPendingNext(true);
			return;
		}
		go(1);
	};

	const setWidth = (w: number | null) => {
		if (!w || w <= 0) return;
		const maxW = item.free ? laizeCm : Math.min(laizeCm, round1(maxPlancheCm * item.aspect));
		const v = round1(Math.min(w, maxW));
		onChange(item.id, item.free ? { widthCm: v } : { widthCm: v, heightCm: round1(v / item.aspect) });
	};
	const setHeight = (h: number | null) => {
		if (!h || h <= 0) return;
		const maxH = item.free ? maxPlancheCm : Math.min(maxPlancheCm, round1(laizeCm / item.aspect));
		const v = round1(Math.min(h, maxH));
		onChange(item.id, item.free ? { heightCm: v } : { heightCm: v, widthCm: round1(v * item.aspect) });
	};

	const tourSteps: TourProps['steps'] = [
		{ title: t('prep-tour-bienvenue-t'), description: t('prep-tour-bienvenue-d', { n: steps.length }) },
		{ title: t('prep-tour-apercu-t'), description: t('prep-tour-apercu-d'), target: () => document.querySelector('.dtf-prep-preview') as HTMLElement },
		{ title: t('prep-tour-boutons-t'), description: t('prep-tour-boutons-d'), target: () => document.querySelector('.dtf-prep-footer') as HTMLElement },
	];

	const hasAB = !!beforeSrc && beforeSrc !== item.src && !busyKind;
	const preview = (
		<div className="dtf-prep-preview-col">
			<div className={`dtf-prep-preview${busyKind ? ' is-busy' : ''}`}>
				<img src={showBefore && beforeSrc ? beforeSrc : item.src} alt="" />
				{item.improved && !showBefore && <span className="dtf-improved-badge">✨ {t('image-amelioree')}</span>}
				{busyKind && (
					<div className="dtf-prep-busy" role="status" aria-live="polite" data-testid="prep-busy">
						<Marmotte pose="magic" tone="info" size={60} className="dtf-marmotte-ia dtf-prep-busy-mmt" typing title={t('prep-busy-titre')}>
							{t(`prep-busy-${busyKind}`)}
						</Marmotte>
						<div className="dtf-prep-busy-bar" aria-hidden="true"><span /></div>
					</div>
				)}
			</div>
			{hasAB && (
				<div className="dtf-prep-ab" role="group" aria-label={t('prep-ab')} data-testid="prep-ab">
					<button type="button" className={`dtf-prep-ab-btn${showBefore ? ' is-on' : ''}`} aria-pressed={showBefore} onClick={() => setShowBefore(true)}>{t('prep-avant')}</button>
					<button type="button" className={`dtf-prep-ab-btn${!showBefore ? ' is-on' : ''}`} aria-pressed={!showBefore} onClick={() => setShowBefore(false)}>{t('prep-apres')}</button>
				</div>
			)}
		</div>
	);
	const maxCmTxt = q && q.maxCm !== null ? fmtCm(q.maxCm) : null;
	const upscaleOk = q ? canUpscale(q.pxW, q.pxH) : false;
	const canReduce = !!q && q.maxCm !== null && q.maxCm < item.widthCm;
	const predictTxt = (() => {
		if (!q || !upscaleOk) return null;
		const { w } = predictUpscalePx(q.pxW, q.pxH);
		const dpi = Math.round(w / (item.widthCm / 2.54));
		return dpi >= minDpi ? t('prep-nettete-apres-ia') : t('prep-nettete-apres-ia-insuffisant', { max: fmtCm(Math.floor((w / minDpi) * 2.54 * 10) / 10) });
	})();

	// Affichage pur : l'écran courant compte même s'il a été atteint par l'état initial. Le total n'est FIGÉ qu'une fois
	// l'analyse terminée (avant, la liste des écrans bouge : netteté, traits…) et ne descend jamais ensuite (effet, pas de
	// mutation pendant le rendu) ; tant qu'il n'est pas connu, on affiche « Étape n » sans total.
	// 17.10.2026 (test personas) : l'écran courant est toujours compté comme vu, même le premier (sinon « Étape 1 sur 5 »
	// s'affichait deux fois : largeur puis fond).
	useEffect(() => { setVisited(v => (v.includes(step) ? v : [...v, step])); }, [step]);
	const seenNow = visited.includes(step) ? visited : [...visited, step];
	const stepIndex = seenNow.filter(k => ORDER.indexOf(k) <= ORDER.indexOf(step)).length;
	const estimate = seenNow.length + steps.filter(k => ORDER.indexOf(k) > ORDER.indexOf(step)).length;
	useEffect(() => {
		if (!analysing && estimate > stepTotalSeen) setStepTotalSeen(estimate);
	}, [analysing, estimate, stepTotalSeen]);
	const stepTotal = stepTotalSeen || (analysing ? 0 : estimate);
	const titleOf: Record<StepKey, string> = {
		taille: t('prep-q-taille-t'),
		fond: t('prep-q-fond-t'),
		finition: t('prep-fin-t'),
		traits: t('prep-q-traits-t'),
		pret: t('prep-q-pret-t'),
	};
	const fondDirty = !!(sessRef.current && sessRef.current.dirty);
	// ---- Écran Finition (Daniel 20.10.2026) ----
	const [finDone, setFinDone] = useState(false);
	const [finChosen, setFinChosen] = useState<'vec' | 'smooth' | 'up' | null>(null);
	const flat = verdict.ok;
	const finAnalysisKey = !metrics ? 'prep-fin-a-inconnu' : flat ? 'prep-fin-a-unies' : verdict.reason === 'degrade' ? 'prep-fin-a-degrade' : verdict.reason === 'petit' ? 'prep-fin-a-petite' : 'prep-fin-a-inconnu';
	// Conseil : trop petite → améliorer (ou réduire) ; dégradés / photo → améliorer tant qu'elle n'est pas très bonne, sinon lisser si
	// une couleur a été retirée ; couleurs unies et nette → vectoriser. La vectorisation n'est jamais liée au seuil de 300 (revue Neo).
	const finReco: 'vec' | 'smooth' | 'up' | null = !metrics ? null
		: dpiBad ? (upscaleOk ? 'up' : null)
		: flat ? (onVectorize ? 'vec' : (fondDirty && onSmooth ? 'smooth' : null))
		: (dpiNow !== null && dpiNow < SOFT_DPI && upscaleOk) ? 'up' : (fondDirty && onSmooth ? 'smooth' : (upscaleOk ? 'up' : null));
	const finCards = [
		{ id: 'vec' as const, title: t('prep-fin-vec'), desc: t('prep-fin-vec-d'), sub: t('prep-fin-gratuit-minute'), busy: vectorizing, busyKey: 'prep-fin-vec-busy',
			disabled: !onVectorize || (!!metrics && !flat), why: !onVectorize ? '' : verdict.reason === 'degrade' ? t('prep-fin-vec-off-degrade') : verdict.reason === 'petit' ? t('prep-fin-vec-off-petite') : '',
			onClick: () => { startAction(); setFinChosen('vec'); setFinDone(true); if (onVectorize) onVectorize(item.id); } },
		{ id: 'smooth' as const, title: t('prep-fin-smooth'), desc: t('prep-fin-smooth-d'), sub: t('prep-fin-immediat'), busy: smoothing, busyKey: 'prep-fin-smooth-busy',
			disabled: !onSmooth, why: '',
			onClick: async () => { startAction(); setFinChosen('smooth'); if (onSmooth) { const ok = await onSmooth(item.id); if (ok) setFinDone(true); else setFinChosen(null); } } },
		{ id: 'up' as const, title: t('prep-fin-up'), desc: t(dpiBad ? 'prep-fin-up-d-petite' : 'prep-fin-up-d'), sub: upscaleOk ? t('prep-fin-gratuit-minute') : '', busy: upscaling, busyKey: 'prep-fin-up-busy',
			disabled: !upscaleOk, why: upscaleOk ? '' : t('prep-fin-up-off'),
			onClick: () => { startAction(); setFinChosen('up'); setFinDone(true); setImprovedHere(true); onUpscale(item.id); } },
	];

	// Netteté : une recommandation principale, l'autre solution en secondaire, « garder » en tertiaire — tous de vrais boutons
	const upscaleBtn = (primary: boolean) => (
		<Button type={primary ? 'primary' : 'default'} size="large" className="dtf-upscale-btn dtf-prep-big" data-primary={primary ? 'true' : undefined} loading={upscaling} onClick={() => { setImprovedHere(true); onUpscale(item.id); }} block>
			✨ {t('prep-nettete-ameliorer')}
		</Button>
	);
	const reduceBtn = (primary: boolean) => (
		<Button type={primary ? 'primary' : 'default'} size="large" className="dtf-redim-btn dtf-prep-big" data-primary={primary ? 'true' : undefined} onClick={() => setWidth(q!.maxCm as number)} block>
			📏 {t('prep-nettete-reduire', { max: maxCmTxt })}
		</Button>
	);

	return (
		<Modal
			centered
			open
			title={
				<span className="dtf-prep-title">
					🧭 {t('prep-titre')}
					<span className="dtf-prep-title-name">{suggestName(item.name) || t('prep-logo-sans-nom')}</span>
					<Button size="small" className="dtf-modal-tour-btn" title={t('tour-revoir-mini')} onClick={() => setTourOpen(true)}>
						{t('prep-aide-btn')}
					</Button>
				</span>
			}
			footer={null}
			width={900}
			className="dtf-prep-modal"
			onCancel={() => onClose(false)}
			maskClosable={false}
		>
			<header className="dtf-prep-head">
				<div className="dtf-prep-progress" data-step={step} aria-label={stepTotal ? t('prep-etape-n', { n: stepIndex, total: stepTotal }) : t('prep-etape-sans-total', { n: stepIndex })}>
					{Array.from({ length: stepTotal || steps.length }, (_, i) => (
						<span key={i} className={`dtf-prep-dot${i === stepIndex - 1 ? ' on' : ''}${i < stepIndex - 1 ? ' done' : ''}`} />
					))}
					<span className="dtf-prep-count">{step === 'pret' ? t('prep-etape-derniere') : stepTotal ? t('prep-etape-n', { n: stepIndex, total: stepTotal }) : t('prep-etape-sans-total', { n: stepIndex })}</span>
				</div>
				<h2 className="dtf-prep-question">{titleOf[step]}</h2>
			</header>
			<div className="dtf-prep-scroll dtf-prep-body" ref={scrollRef}>
				{step === 'taille' && (
					<div className="dtf-prep-taille dtf-prep-step">
						{preview}
						<div className="dtf-prep-taille-form">
							<p className="dtf-prep-hint">{t('prep-q-taille-aide')}</p>
							<div className="dtf-prep-presets">
								{PRESETS.map(p => (
									<button
										key={p.k}
										type="button"
										className={`dtf-prep-preset${Math.abs(item.widthCm - p.cm) < 0.05 ? ' on' : ''}`}
										onClick={() => {
											setByHeight(false);
											setWidth(p.cm);
										}}
									>
										{t(`prep-preset-${p.k}`)}
									</button>
								))}
							</div>
							{!byHeight ? (
								<div className="dtf-prep-width-row">
									<label htmlFor="dtfPrepWidth">{t('prep-largeur')}</label>
									<InputNumber id="dtfPrepWidth" className="dtf-prep-width" size="large" min={1} max={laizeCm} step={0.5} parser={commaParser} value={item.widthCm} onChange={setWidth} />
									<span className="dtf-prep-auto">{t('prep-hauteur-auto', { h: fmtCm(item.heightCm) })}</span>
								</div>
							) : (
								<div className="dtf-prep-width-row">
									<label htmlFor="dtfPrepHeight">{t('prep-hauteur-cm')}</label>
									<InputNumber id="dtfPrepHeight" className="dtf-prep-height" size="large" min={1} max={maxPlancheCm} step={0.5} parser={commaParser} value={item.heightCm} onChange={setHeight} />
									<span className="dtf-prep-auto">{t('prep-largeur-auto', { w: fmtCm(item.widthCm) })}</span>
								</div>
							)}
							<Button size="large" className="dtf-prep-link dtf-prep-alt" onClick={() => setByHeight(v => !v)}>
								{byHeight ? t('prep-plutot-largeur') : t('prep-plutot-hauteur')}
							</Button>
							<p className="dtf-prep-hint dtf-prep-hint-small">{t('prep-taille-max', { laize: fmtCm(laizeCm) })}</p>
						</div>
					</div>
				)}
				{step === 'fond' && (
					<div className="dtf-prep-couleurs dtf-prep-step">
						<BgRemovePanel src={item.src} sessRef={sessRef} onChanged={bump} simple />
					</div>
				)}
				{step === 'finition' && (
					<div className="dtf-prep-decision dtf-prep-step dtf-prep-finition">
						{preview}
						<div className="dtf-prep-decision-col">
							{analysing || !metrics ? (
								<p className="dtf-prep-hint">{t('prep-analyse')}</p>
							) : (
								<>
									<div className="dtf-fin-facts">
										<div><b>{t('prep-fin-nettete')}</b> {dpiNow !== null ? t('prep-fin-ppp', { dpi: dpiNow, w: fmtCm(item.widthCm) }) : '—'} — {t(dpiBad ? 'prep-fin-v-petite' : dpiNow !== null && dpiNow < SOFT_DPI ? 'prep-fin-v-correcte' : 'prep-fin-v-bonne', { min: minDpi })}</div>
										<div><b>{t('prep-fin-analyse')}</b> {t(finAnalysisKey)}</div>
										<details className="dtf-prep-details"><summary>{t('prep-pourquoi-tech')}</summary><p>{q && t('prep-nettete-detail', { px: q.pxW, py: q.pxH, w: fmtCm(item.widthCm), dpi: dpiNow, min: minDpi })}</p></details>
									</div>
									{busyKind ? (
										<Marmotte pose="magic" tone="info" size={56} className="dtf-marmotte-ia" typing title={t('prep-busy-titre')}>
											{t(`prep-busy-${busyKind}`)}
										</Marmotte>
									) : finChosen && hasAB ? (
										<Marmotte pose="thumbs" tone="ok" size={56} className="dtf-marmotte-ia" title={t(`prep-apres-ok-${finChosen}-t`)}>
											{t('prep-apres-ok')}
										</Marmotte>
									) : (
										<Marmotte pose={finReco ? 'thumbs' : 'thinking'} tone={finReco ? 'ok' : 'warn'} size={56} className="dtf-marmotte-ia" title={t(finReco ? `prep-fin-reco-${finReco}-t` : 'prep-fin-reco-none-t')}>
											{t(finReco ? `prep-fin-reco-${finReco}` : 'prep-fin-reco-none', { max: maxCmTxt })}
											<TutoLink n={5} />
										</Marmotte>
									)}
									<div className="dtf-fin-cards" role="group" aria-label={t('prep-fin-t')}>
										{finCards.map(c => (
											<button
												key={c.id}
												type="button"
												className={`dtf-fin-card${finReco === c.id ? ' dtf-fin-reco' : ''}${c.disabled ? ' dtf-fin-off' : ''}${finChosen === c.id ? ' dtf-fin-chosen' : ''}`}
												data-testid={`fin-${c.id}`}
												disabled={c.disabled || c.busy}
												aria-pressed={finChosen === c.id}
												onClick={c.onClick}
											>
												{finReco === c.id && <span className="dtf-fin-tag">{t('prep-fin-conseille')}</span>}
												<b>{c.busy ? t(c.busyKey) : c.title}</b>
												<span>{c.desc}</span>
												{c.sub && <small>{c.sub}</small>}
												{c.disabled && c.why && <em>{c.why}</em>}
											</button>
										))}
									</div>
									<div className="dtf-prep-actions dtf-choice-stack">
										{dpiBad && canReduce && reduceBtn(false)}
										{/* Daniel 20.10 : une fois une finition faite, « Suivant » suffit — le bouton « Garder tel quel » n'a plus de sens */}
										{!finDone && (
											<Button size="large" className="dtf-prep-garder dtf-prep-tertiary" data-testid="fin-keep" onClick={() => { setAcceptBlur(true); setAcceptRaster(true); }} block>
												{t('prep-fin-garder')}
											</Button>
										)}
									</div>
								</>
							)}
						</div>
					</div>
				)}
				{step === 'traits' && (
					<div className="dtf-prep-decision dtf-prep-step">
						{preview}
						<div className="dtf-prep-decision-col">
							{fixingThin ? (
								<Marmotte pose="magic" tone="info" size={56} className="dtf-marmotte-ia" typing title={t('prep-busy-titre')}>
									{t('prep-busy-thin')}
								</Marmotte>
							) : fixedHere ? (
								<>
									<Marmotte pose="thumbs" tone="ok" size={56} className="dtf-marmotte-ia" title={t('prep-apres-ok-thin-t')}>
										{t('prep-apres-ok')}
									</Marmotte>
									{needTraits && !analysing && <p className="dtf-prep-hint dtf-traits-encore" role="status">{t('prep-traits-encore')}</p>}
									<div className="dtf-prep-actions dtf-choice-stack">
										<Button type="primary" size="large" className="dtf-prep-next dtf-prep-big" data-primary="true" data-testid="traits-continuer" onClick={() => go(1)} block>
											{t('prep-continuer')} →
										</Button>
										{needTraits && !analysing && (
											<Button size="large" className="dtf-prep-tertiary dtf-thinfix-again" onClick={() => setFixedHere(false)} block>
												{t('prep-autre-solution')}
											</Button>
										)}
									</div>
								</>
							) : analysing ? (
								<p className="dtf-prep-hint">{t('prep-analyse')}</p>
							) : (
								<>
									<Marmotte pose="thinking" tone="warn" size={56} className="dtf-marmotte-ia" title={t('prep-q-traits-t')}>
										{t('prep-traits-aide')}
									</Marmotte>
									<div className="dtf-prep-actions dtf-choice-stack">
										{q && q.minCm !== null && q.minCm !== Infinity && q.minCm > item.widthCm && (q.maxCm === null || q.minCm <= q.maxCm) && (
											<Button size="large" className="dtf-redim-btn dtf-prep-big" onClick={() => setWidth(q.minCm as number)} block>
												📏 {t('redim-min', { min: fmtCm(q.minCm) })}
											</Button>
										)}
										<Button type="primary" size="large" className="dtf-thinfix-btn dtf-thinfix-thicken dtf-prep-big" data-primary="true" loading={fixingThin} onClick={() => void fixHere('thicken')} block>
											🖌 {t('prep-traits-epaissir')}
										</Button>
										<div className="dtf-quality-backing-row">
											<Button size="large" className="dtf-thinfix-btn dtf-thinfix-backing dtf-prep-big" loading={fixingThin} onClick={() => void fixHere('backing')} block>
												👕 {t('prep-traits-fond')}
											</Button>
											<label className="dtf-garment-pick dtf-garment-pick-big" title={t('fond-couleur')}>
												<span>{t('fond-couleur-court')}</span>
												<input type="color" className="dtf-garment-color" value={garmentColor} onChange={e => setGarmentColor(e.target.value)} />
											</label>
										</div>
										<Button size="large" className="dtf-prep-garder dtf-prep-tertiary" onClick={() => setAcceptThin(true)} block>
											{t('prep-garder-risque')}
										</Button>
									</div>
									<details className="dtf-prep-details">
										<summary>{t('prep-pourquoi-tech')}</summary>
										<p>{t('prep-traits-detail')}</p>
									</details>
								</>
							)}
						</div>
					</div>
				)}
				{step === 'pret' && (
					<div className="dtf-prep-decision dtf-prep-pret dtf-prep-step">
						{preview}
						<div className="dtf-prep-decision-col">
							{analysing ? (
								<p className="dtf-prep-hint">{t('prep-analyse')}</p>
							) : (
							<Marmotte pose="thumbs" tone="ok" size={56} className="dtf-marmotte-ia" title={t('prep-q-pret-t')}>
								{t('prep-pret-texte')}
							</Marmotte>
							)}
							<ul className="dtf-prep-recap">
								<li>{t('prep-recap-taille', { w: fmtCm(item.widthCm), h: fmtCm(item.heightCm) })}</li>
								{!isVector && <li>{t('prep-recap-fond', { v: t(fondChoice === 'enleve' ? 'prep-v-enleve' : 'prep-v-garde') })}</li>}
								<li>{t('prep-recap-nettete', { v: t(isVector ? 'prep-v-vectoriel' : acceptBlur && dpiBad ? 'prep-v-risque' : improvedHere ? 'prep-v-amelioree' : 'prep-v-ok') })}</li>
								<li>{t('prep-recap-traits', { v: t(acceptThin && needTraits ? 'prep-v-risque' : fixedHere ? 'prep-v-corriges' : 'prep-v-aucun') })}</li>
								{fixedHere && needTraits && !analysing && <li className="dtf-traits-encore">{t('prep-traits-encore')}</li>}
								<li>{t('prep-recap-format', { v: t(isVector || item.vectorized ? 'prep-v-format-vecto' : 'prep-v-format-image') })}</li>
							</ul>
							{!item.library && (
								<div className="dtf-prep-keep">
									{canSave ? (
										<>
											<label className="dtf-prep-keep-name" htmlFor="dtfPrepLogoName">
												{t('prep-nom-logo')}
												<Input id="dtfPrepLogoName" size="large" maxLength={80} value={logoName} placeholder={t('prep-nom-logo-ph')} onChange={e => setLogoName(e.target.value)} />
											</label>
											<Checkbox className="dtf-prep-keep-check" checked={keep} onChange={e => setKeep(e.target.checked)}>
												📚 {t('prep-garder-biblio')}
											</Checkbox>
											<p className="dtf-prep-hint dtf-prep-hint-small">{t(keep ? 'prep-garder-biblio-aide' : 'prep-garder-biblio-non')}</p>
										</>
									) : (
										<p className="dtf-prep-hint dtf-prep-keep-login">
											📚 {t('prep-garder-connexion')}{' '}
											<a href={`/login.html?next=${encodeURIComponent('/editeur-v2/')}`}>{t('prep-garder-connexion-lien')}</a>
										</p>
									)}
								</div>
							)}
						</div>
					</div>
				)}
			</div>
			<footer className="dtf-prep-footer">
				<Button size="large" className="dtf-prep-prev" disabled={steps.indexOf(step) === 0} onClick={() => go(-1)}>
					← {t('prep-precedent')}
				</Button>
				{step === 'pret' ? (
					<Button type="primary" size="large" className="dtf-prep-finish" data-primary="true" onClick={() => onClose(true, canSave && !item.library ? { keep, name: logoName.trim() || suggestName(item.name) || 'Logo' } : undefined)}>
						✅ {t('prep-mettre-planche')}
					</Button>
				) : step === 'traits' || (step === 'finition' && !finDone) ? (
					<span className="dtf-prep-footer-hint">{step === 'traits' && fixedHere ? '' : t('prep-choisis-au-dessus')}</span>
				) : (
					<Button type="primary" size="large" className="dtf-prep-next" data-primary="true" loading={pendingNext || (step === 'finition' && busy)} onClick={next}>
						{step === 'fond' ? (fondDirty ? t('prep-fond-oui') : t('prep-fond-garder')) : step === 'finition' && upscaling ? t('prep-fin-up-busy') : step === 'finition' && vectorizing ? t('prep-fin-vec-busy') : t('prep-suivant')} →
					</Button>
				)}
			</footer>
			<Tour open={tourOpen} onClose={() => setTourOpen(false)} steps={tourSteps} />
		</Modal>
	);
};

export default PrepWizard;
